// askSmart-ui/src/components/SMRTDocsFieldPlacementModal.tsx
//
// Field placement experience for SMRT Docs templates. Admin opens this on
// an uploaded document, drops Signature / Initial / Date / Title / Text
// fields onto the page, assigns each to a positional signer, and drags
// them where they should appear during signing.
//
// Scope is placement only — the modal stores normalized x/y coordinates
// (0–1, relative to the canvas) and the signer index against each field.
// Signing behavior reads these in a follow-up stage.

import React, { useEffect, useRef, useState } from "react";
import { loadPdfjs } from "../utils/pdfWorker";

export type SMRTDocsPlacedFieldType =
  | "signature"
  | "initial"
  | "date"
  | "title"
  | "text"
  // Behaves exactly like "text" for the signer, but the captured value is
  // encrypted at-rest (same envelope as sensitive Profile fields), masked in
  // the signed PDF, and only decryptable via the audited reveal path. Reusable
  // for SIN, banking, licence numbers and any other sensitive free-text.
  | "text-encrypted";

export interface SMRTDocsPlacedField {
  id: string;
  type: SMRTDocsPlacedFieldType;
  signerIndex: number;
  // Normalized [0, 1] relative to the page identified by `page`. Keeps
  // positions stable across viewport widths without storing a fixed
  // pixel offset, and stays well-defined per-page on multi-page docs.
  x: number;
  y: number;
  // Zero-based page index this placement belongs to. Optional for
  // backward compatibility with placements saved before multi-page
  // support — treated as 0 when absent.
  page?: number;
}

interface SignerOption {
  // Either a real userId resolved at issue-time, or "" for positional
  // signers picked before signers are known.
  userId?: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  documentBlobName: string;
  documentName: string;
  ownerUserId: string;
  signers: SignerOption[];
  initialPlacements: SMRTDocsPlacedField[];
  onClose: () => void;
  onSave: (placements: SMRTDocsPlacedField[]) => void;
}

const FIELD_TYPES: { value: SMRTDocsPlacedFieldType; label: string }[] = [
  { value: "signature", label: "Signature" },
  { value: "initial", label: "Initial" },
  { value: "date", label: "Date" },
  { value: "title", label: "Title" },
  { value: "text", label: "Text" },
  { value: "text-encrypted", label: "Text (Encrypted)" },
];

// Colors are cycled by signerIndex so each signer's fields are visually
// distinct. Bounded list — admins with >5 signers wrap.
const SIGNER_COLORS = [
  "#4a90e2",
  "#e2724a",
  "#50c878",
  "#b768e2",
  "#e2c14a",
];

// Per-type placement footprint in pixels at the modal's 800-wide render.
// Signature mirrors the PDF embed box (140 × 36 — see smrtdocsPdf.ts
// BOX_W_FACTOR/BOX_H_FACTOR). The remaining sizes are realistic guides
// for typical content so admins can place without overlapping document
// text. The PDF text-draw position is independent of these heights, so
// shrinking non-signature boxes doesn't shift the signed output.
const FIELD_DIMS: Record<
  SMRTDocsPlacedFieldType,
  { width: number; height: number }
> = {
  signature: { width: 140, height: 36 },
  initial: { width: 70, height: 36 },
  date: { width: 90, height: 22 },
  title: { width: 150, height: 22 },
  text: { width: 180, height: 22 },
  "text-encrypted": { width: 180, height: 22 },
};

const FIELD_LABEL: Record<SMRTDocsPlacedFieldType, string> = {
  signature: "Sign",
  initial: "Initial",
  date: "Date",
  title: "Title",
  text: "Text",
  "text-encrypted": "Text 🔒",
};

const PencilIcon = () => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function SMRTDocsFieldPlacementModal({
  isOpen,
  documentBlobName,
  documentName,
  ownerUserId,
  signers,
  initialPlacements,
  onClose,
  onSave,
}: Props) {
  const [activeSignerIndex, setActiveSignerIndex] = useState(0);
  const [placements, setPlacements] = useState<SMRTDocsPlacedField[]>([]);
  // When non-null, the next click on the document drops a field of this
  // type at the cursor instead of immediately placing on type select. A
  // ghost preview tracks the cursor while this is armed.
  const [placingType, setPlacingType] =
    useState<SMRTDocsPlacedFieldType | null>(null);
  const [previewPos, setPreviewPos] = useState<
    { x: number; y: number; page: number } | null
  >(null);
  // Rendered page bitmaps + native sizes. The wrapper is scrollable, so
  // an iframe (whose pointer-events would have to be disabled, killing
  // scroll) is replaced with pdf.js-rendered page images stacked
  // vertically.
  const [pages, setPages] = useState<
    { width: number; height: number; imageUrl: string }[]
  >([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // The inner pages stack is centered inside the wrapper with
  // `margin: 0 auto`, so when the wrapper is wider than the rendered
  // page there's a gutter on each side. All placement math has to run
  // relative to this container — measuring from the wrapper instead
  // would shift the preview/drag by the gutter width.
  const pagesContainerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    id: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const seededRef = useRef(false);

  // Pull the document blob once per open and render every page via
  // pdf.js into a data-URL so the modal can present the full document
  // scrollably (the previous iframe + pointer-events: none combo blocked
  // the PDF viewer's own scrollbar).
  useEffect(() => {
    if (!isOpen) {
      setPages([]);
      return;
    }
    if (!documentBlobName) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/smrtdocs/files/${encodeURIComponent(
            ownerUserId,
          )}/_?blobName=${encodeURIComponent(documentBlobName)}&format=base64`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error("Failed to fetch document");
        const { base64 } = await res.json();
        const bin = atob(base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const pdfjs = await loadPdfjs();
        const doc = await pdfjs.getDocument({ data: bytes }).promise;
        const TARGET_WIDTH = 800;
        const rendered: {
          width: number;
          height: number;
          imageUrl: string;
        }[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = TARGET_WIDTH / base.width;
          const vp = page.getViewport({ scale });
          const c = document.createElement("canvas");
          c.width = vp.width;
          c.height = vp.height;
          const ctx = c.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          rendered.push({
            width: vp.width,
            height: vp.height,
            imageUrl: c.toDataURL("image/png"),
          });
        }
        if (cancelled) return;
        setPages(rendered);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to render PDF for placement:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, ownerUserId, documentBlobName]);

  // On open, hydrate from initial placements. When the doc has none yet
  // we pre-arm the Signature tool so the admin can click the page to
  // drop their first field — no auto-seeded placeholder.
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false;
      setPlacingType(null);
      setPreviewPos(null);
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    setActiveSignerIndex(0);
    setPlacements(initialPlacements);
    if (initialPlacements.length === 0) {
      setPlacingType("signature");
    }
  }, [isOpen, initialPlacements]);

  // Escape cancels an armed placement without dropping a field.
  useEffect(() => {
    if (!isOpen || !placingType) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPlacingType(null);
        setPreviewPos(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, placingType]);

  if (!isOpen) return null;

  const armField = (type: SMRTDocsPlacedFieldType) => {
    setPlacingType(type);
    setPreviewPos(null);
  };

  const onFieldPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    field: SMRTDocsPlacedField,
  ) => {
    e.preventDefault();
    const fieldEl = e.currentTarget;
    const fieldRect = fieldEl.getBoundingClientRect();
    dragRef.current = {
      id: field.id,
      offsetX: e.clientX - fieldRect.left,
      offsetY: e.clientY - fieldRect.top,
    };
    fieldEl.setPointerCapture(e.pointerId);
  };

  // Walk the per-page stack to map a wrapper-relative content Y to its
  // page index + the local y offset within that page.
  const resolvePage = (contentY: number) => {
    let cumulative = 0;
    let pageIdx = pages.length - 1;
    let localY = 0;
    for (let i = 0; i < pages.length; i++) {
      if (contentY < cumulative + pages[i].height) {
        pageIdx = i;
        localY = contentY - cumulative;
        break;
      }
      cumulative += pages[i].height;
      if (i === pages.length - 1) localY = pages[i].height;
    }
    return { pageIdx, localY };
  };

  const onCanvasPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const pagesContainer = pagesContainerRef.current;
    if (!pagesContainer) return;
    if (pages.length === 0) return;
    // Measure against the pages container, not the wrapper. The
    // wrapper can be wider than the rendered page (the inner stack is
    // `margin: 0 auto`), and using the wrapper's left edge would
    // bake that gutter into every coordinate. getBoundingClientRect()
    // on the inner container already accounts for scroll, so the math
    // no longer needs scrollLeft/scrollTop adjustments either.
    const pagesRect = pagesContainer.getBoundingClientRect();

    const drag = dragRef.current;
    if (drag) {
      const contentX = e.clientX - pagesRect.left - drag.offsetX;
      const contentY = e.clientY - pagesRect.top - drag.offsetY;
      const { pageIdx, localY } = resolvePage(contentY);
      const page = pages[pageIdx];
      const nx = Math.max(0, Math.min(1, contentX / page.width));
      const ny = Math.max(0, Math.min(1, localY / page.height));
      setPlacements((prev) =>
        prev.map((p) =>
          p.id === drag.id ? { ...p, x: nx, y: ny, page: pageIdx } : p,
        ),
      );
      return;
    }

    if (placingType) {
      // Center the preview on the cursor: shift by half the box so the
      // box's center tracks the pointer, not its top-left corner.
      const dims = FIELD_DIMS[placingType];
      const contentX = e.clientX - pagesRect.left - dims.width / 2;
      const contentY = e.clientY - pagesRect.top - dims.height / 2;
      const { pageIdx, localY } = resolvePage(contentY);
      const page = pages[pageIdx];
      const nx = Math.max(0, Math.min(1, contentX / page.width));
      const ny = Math.max(0, Math.min(1, localY / page.height));
      setPreviewPos({ x: nx, y: ny, page: pageIdx });
    }
  };

  const onCanvasPointerUp = () => {
    dragRef.current = null;
  };

  const onCanvasClick = () => {
    if (!placingType || !previewPos) return;
    setPlacements((prev) => [
      ...prev,
      {
        id: newId(),
        type: placingType,
        signerIndex: activeSignerIndex,
        x: previewPos.x,
        y: previewPos.y,
        page: previewPos.page,
      },
    ]);
    setPlacingType(null);
    setPreviewPos(null);
  };

  const removeField = (id: string) => {
    setPlacements((prev) => prev.filter((p) => p.id !== id));
  };

  const signerLabel = (i: number) => signers[i]?.name || `Signer ${i + 1}`;
  const signerColor = (i: number) =>
    SIGNER_COLORS[i % SIGNER_COLORS.length];

  return (
    <div className="smrtdocs-modal-overlay" style={{ zIndex: 10000 }}>
      <div
        className="smrtdocs-modal"
        style={{
          width: "92vw",
          height: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            Place Fields — {documentName}
          </h2>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid #2a2a2a",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <label style={{ color: "#9aa0a6", fontSize: "0.85rem" }}>
            Signer
          </label>
          <select
            className="manage-input"
            style={{ width: 260 }}
            value={activeSignerIndex}
            onChange={(e) =>
              setActiveSignerIndex(parseInt(e.target.value, 10) || 0)
            }
          >
            {signers.map((_, i) => (
              <option key={i} value={i}>
                {signerLabel(i)}
              </option>
            ))}
          </select>
          <span
            aria-label="Signer color"
            style={{
              display: "inline-block",
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: signerColor(activeSignerIndex),
              border: "1px solid #111",
            }}
          />
        </div>

        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div
            style={{
              width: 170,
              borderRight: "1px solid #2a2a2a",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                color: "#9aa0a6",
                fontSize: "0.8rem",
                marginBottom: 4,
              }}
            >
              Field Types
            </div>
            {FIELD_TYPES.map((t) => (
              <button
                key={t.value}
                className={
                  placingType === t.value ? "btn-save" : "btn-cancel"
                }
                style={{ width: "100%", textAlign: "left" }}
                onClick={() => armField(t.value)}
              >
                + {t.label}
              </button>
            ))}
            {placingType && (
              <div
                style={{
                  marginTop: 8,
                  padding: "6px 8px",
                  background: "#2a2a2a",
                  borderRadius: 4,
                  color: "#9aa0a6",
                  fontSize: "0.72rem",
                  lineHeight: 1.3,
                }}
              >
                Click on the document to place this {placingType}. Press
                Esc to cancel.
              </div>
            )}
          </div>

          <div
            ref={wrapperRef}
            style={{
              flex: 1,
              position: "relative",
              background: "#1c1c1c",
              // Scrollable wrapper — replaces the prior overflow:hidden
              // iframe so admins can navigate tall / multi-page docs.
              overflow: "auto",
              cursor: placingType ? "crosshair" : "default",
            }}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onClick={onCanvasClick}
          >
            {pages.length === 0 ? (
              <div style={{ padding: 20, color: "#9aa0a6" }}>
                Loading document…
              </div>
            ) : (
              <div
                ref={pagesContainerRef}
                style={{
                  position: "relative",
                  width: pages[0].width,
                  margin: "0 auto",
                }}
              >
                {pages.map((pg, i) => (
                  <img
                    key={i}
                    src={pg.imageUrl}
                    alt={`${documentName} page ${i + 1}`}
                    width={pg.width}
                    height={pg.height}
                    draggable={false}
                    style={{
                      display: "block",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  />
                ))}

                {placements.map((p) => {
                  const color = signerColor(p.signerIndex);
                  const pageIdx =
                    typeof p.page === "number" ? p.page : 0;
                  const page = pages[pageIdx];
                  if (!page) return null;
                  const pageTop = pages
                    .slice(0, pageIdx)
                    .reduce((acc, x) => acc + x.height, 0);
                  const dims = FIELD_DIMS[p.type];
                  const stacked =
                    p.type === "signature" || p.type === "initial";
                  return (
                    <div
                      key={p.id}
                      onPointerDown={(e) => onFieldPointerDown(e, p)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        left: p.x * page.width,
                        top: pageTop + p.y * page.height,
                        width: dims.width,
                        height: dims.height,
                        background: "rgba(255, 255, 255, 0.92)",
                        border: stacked
                          ? `1px solid ${color}99`
                          : `2px solid ${color}`,
                        borderLeft: `4px solid ${color}`,
                        borderRadius: 4,
                        color: "#111",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "move",
                        userSelect: "none",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                      }}
                      title={`${signerLabel(p.signerIndex)} — ${p.type}`}
                    >
                      {stacked ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            gap: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "0.72rem",
                              fontWeight: 600,
                            }}
                          >
                            {FIELD_LABEL[p.type]}
                          </span>
                          <PencilIcon />
                        </div>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            padding: "0 14px 0 6px",
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {FIELD_LABEL[p.type]}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeField(p.id);
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          top: 1,
                          right: 3,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#666",
                          fontSize: "0.75rem",
                          padding: 0,
                          lineHeight: 1,
                        }}
                        aria-label="Remove field"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}

                {placingType &&
                  previewPos &&
                  pages[previewPos.page] &&
                  (() => {
                    const dims = FIELD_DIMS[placingType];
                    const stacked =
                      placingType === "signature" ||
                      placingType === "initial";
                    return (
                      <div
                        style={{
                          position: "absolute",
                          left:
                            previewPos.x * pages[previewPos.page].width,
                          top:
                            pages
                              .slice(0, previewPos.page)
                              .reduce((acc, x) => acc + x.height, 0) +
                            previewPos.y *
                              pages[previewPos.page].height,
                          width: dims.width,
                          height: dims.height,
                          background: "rgba(255, 255, 255, 0.6)",
                          border: stacked
                            ? `1px dashed ${signerColor(
                                activeSignerIndex,
                              )}99`
                            : `2px dashed ${signerColor(
                                activeSignerIndex,
                              )}`,
                          borderLeft: `4px solid ${signerColor(
                            activeSignerIndex,
                          )}`,
                          borderRadius: 4,
                          color: "#111",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                          opacity: 0.85,
                        }}
                      >
                        {stacked ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              lineHeight: 1,
                              gap: 2,
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                              }}
                            >
                              {FIELD_LABEL[placingType]}
                            </span>
                            <PencilIcon />
                          </div>
                        ) : (
                          <span style={{ fontSize: "0.7rem" }}>
                            {FIELD_LABEL[placingType]}
                          </span>
                        )}
                      </div>
                    );
                  })()}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid #2a2a2a",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ color: "#9aa0a6", fontSize: "0.8rem" }}>
            {placements.length} field{placements.length === 1 ? "" : "s"} placed
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-save"
              onClick={() => onSave(placements)}
            >
              Issue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
